/*
 * File: form.ts
 * File Created: 2024-01-11 10:25:02
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-16 12:11:08
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { MixinAuto, BaseComponent } from "@root/components/base-classes";
import { ArrayHelper, IQuery, IQueryDefault, ObjectHelper, Retrier } from "@root/core";
import { MetaAttribute, MetaParser } from "@root/core/server/parser/meta-parser";
import {
    EUITagTarget,
    IUITagInput,
    IUITagResult,
    IUITagVariables,
} from "@root/core/tags";
import { applyTags } from "@root/core/tags/core/apply-tags";
import { PropInnateSync } from "@root/core/vue-props";
import {
    Component,
    Emit,
    Inject,
    InjectReactive,
    Prop,
    Watch,
} from "vue-property-decorator";
import { IFormValidateResult } from "./lib/core";
import { IBaseComponentUITag, IServer } from "@root/components/interfaces";
import { queryReload, throttle } from "@root/core/decorators";
import config from "@/config/default/serverConfig";
import debug from "@/config/default/debug.js";
import { PRIVATE_ROW_ID } from "@root/components/table/core/lib/base-table-core";
import { extractErrorCode, extractErrorFields } from "@root/core/utilities";

export interface IFormData {
    "text-submit"?: string;
    "icon-submit"?: string;
    "variant-submit"?: string;
    "size-submit"?: string;
    /// when validated, do "@submit" automatically
    "auto-submit"?: boolean;
    portal?: string;
}
const defaultData = {
    "text-submit": "w_Submit",
    "icon-submit": "fa-solid fa-circle-check",
    "variant-submit": "primary",
    "size-submit": "lg",
    "auto-submit": false,
};

@Component
export default class Form extends BaseComponent {
    /// public props ///////////////////////
    /// The URI for this request.
    @Prop({ type: Object, required: false })
    server: IServer | undefined;

    @Prop({ type: [String, Object], required: true })
    interface!: string | MetaParser;

    /// value can be any object
    @PropInnateSync("value", { type: Object, required: false, default: () => ({}) })
    innateValue!: any;

    @Prop({ type: Object, required: false })
    uiTag?: IBaseComponentUITag;

    @Prop({ type: Object, required: false })
    declare data: IFormData;
    get mergedData() {
        return {
            ...defaultData,
            ...(this.autoInstance ? { portal: this.autoInstance.autoComponentId } : {}),
            ...this.data,
        };
    }

    /// inject
    // react to component that providing interface
    @Inject({ default: undefined })
    readonly viewInstance: Vue | undefined;

    // react to auto components
    @InjectReactive()
    readonly autoInstance!: MixinAuto | undefined;

    /// available slots /////////////////////
    publicSlots = {};

    /// private helpers
    // updating key of query
    updateIndex: number = 0;

    @Emit("updated")
    doUpdated(value) {
        return value;
    }

    /// @reloaded, happens on after submit query is done.
    @Emit("reloaded")
    doReloaded(value) {
        return ObjectHelper.deepClone(value);
    }

    /// @submit, happpens on button "submit" being clicked and also validated,
    /// and also after the query is done.
    /// on submit, do:
    /// 1) @uiSubmitWrap the innate value as result
    /// 2) if server configured with C / U / D, perform
    /// 3) $emit value
    @Emit("submit")
    async doSubmit() {
        return await this.funcDoSubmit();
    }
    @queryReload()
    funcDoSubmit() {
        return funcDoSubmit.call(this);
    }
    doKeyUp() {
        /// on element keyup, show popover or do submit
        if (this.validated) {
            this.doSubmit();
        } else {
            (this.$refs.popover as any)?.doShow?.();
        }
    }

    // handle auto-submit
    @Watch("innateValue", { immediate: true })
    onInnateValueChanged(newValue: any, oldValue: any) {
        /// clean the prepare for changed cache, if innateValue changed.
        this.cachedValue = null;
        if (this.mergedData["auto-submit"] != true) return;
        if (this.validated == true) {
            this.doSubmit();
        }
    }

    // interface parser
    get metas(): MetaParser {
        if (this.interface instanceof MetaParser) return this.interface;
        return new MetaParser(this.interface);
    }

    refByMetaAttribute(meta: MetaAttribute) {
        const id = `${meta.attr.name}-${meta.attr.index}`;
        let ref = this.$refs[id];
        if (Array.isArray(ref)) ref = ref[0];
        const result = { id, ref };
        return result;
    }
    bindFormProperties(target: keyof typeof EUITagTarget, metaIndex: number) {
        const meta = this.metas.getMetaByIndex(metaIndex);
        if (meta == null) return {}; /// it should not happen

        const result = getFormProperties(
            target,
            this.innateValue,
            this.metas,
            metaIndex,
            meta,
            this.viewInstance,
            this.uiTag,
            this.server,
            this,
            this.refByMetaAttribute(meta).ref as Vue,
        );
        return result;
    }

    // hold the data / loading / error
    query: IQuery = IQueryDefault();
    /// when server changed with param id, do reload.
    @Watch("server", { immediate: true })
    @queryReload()
    onServerUpdated(newValue: IServer | undefined, oldValue: IServer | undefined) {
        return funcOnServerUpdated.call(this, newValue, oldValue);
    }

    /// reload the data related to id.
    @throttle(1000)
    reload() {
        return this.funcDoReload(this.server);
    }
    @queryReload()
    funcDoReload(value: IServer | undefined) {
        return funcDoReload.call(this, value);
    }

    /// update value with possible wrapper
    /// gracefully handle update value.
    /// there could be a second update from @uiProvideRow (relation) changes.
    cachedValue: any = null;
    doUpdateValue(inf, value) {
        /// prepare the retrier
        const retrier = ((this as any).cachedRetrier ??= new Retrier(() => {
            do {
                if (this.cachedValue == null) break;
                /// debugger /////////////////////
                {
                    debug.debugFormAndTable && console.log(`<Form> update value!`);
                }
                //////////////////////////////////
                this.innateValue = {
                    ...this.innateValue,
                    ...this.cachedValue,
                };
                this.cachedValue = null;
            } while (0);

            retrier.stop();
        }, 0));

        const name = inf.attr.name;
        if (ObjectHelper.equals(this.innateValue[name], value)) return;

        /// debugger /////////////////////
        {
            debug.debugFormAndTable &&
                console.log(`<Form> prepare update, name: '${name}', value:`, value);
        }
        //////////////////////////////////

        this.cachedValue ??= {};
        this.cachedValue[name] = value;
        retrier.restart();
    }

    /// update value with possible wrapper
    states: Record<string, NonNullable<IFormValidateResult>> | null = null;
    doUpdateState(inf: MetaAttribute, value) {
        this.states ??= {};
        this.$set(this.states, inf.attr.fullname, value);
    }
    /// button state
    get validated() {
        /// if loading or error, disabled
        //if (this.states == null || this.query.loading || this.query.error) return false;
        /// if loading, disabled
        if (this.states == null || this.query.loading) return false;

        /// every state should be true
        const states = this.states;
        return Object.keys(states).reduce((final, key) => {
            if (final === false) return final;

            /// ignore the @submitIgnore field ///////
            const inf = this.metas.attributeMap[key];
            const binding = this.bindFormProperties("FormElement", inf.attr.index);
            const specials = binding.specials;
            if (specials?.submitIgnore) return true;
            //////////////////////////////////////////

            if (states[key].state === false) return false;
            return final;
        }, true);
    }

    /// handle submit button popovers ///////////////////
    // button popover message
    get invalidMessages(): Array<{ key: string; label: string; message: string }> {
        return invalidMessages.call(this);
    }

    doFocusInvalidField(key: string) {
        return doFocusInvalidField.call(this, key);
    }
    /////////////////////////////////////////////////////

    /// handle submit button popovers ///////////////////
    /// currently, only handle error from the code. (no customization)
    errorHandler(error) {
        if ((error as any)?.handled) return;
        const payload = error?.body ?? error;
        const code = extractErrorCode(payload);
        if (code) {
            const fields = extractErrorFields(payload);
            let messageKey = `e_${code}`;
            if (code === "UNIQUE_CONSTRAINT" && fields.length > 0) {
                const fieldKey = fields[0].toUpperCase();
                const specificKey = `${messageKey}_${fieldKey}`;
                const translated = this.$_(specificKey);
                if (translated !== specificKey) {
                    messageKey = specificKey;
                }
            }
            this.$alert(
                {
                    title: this.$_("w_Error"),
                    message: this.$_(messageKey),
                },
                "danger",
            );
        }
    }
    /////////////////////////////////////////////////////
}

function getFormProperties(
    target: keyof typeof EUITagTarget,
    data: any,
    metas?: MetaParser,
    metaIndex?: number,
    meta?: MetaAttribute,
    component?: Vue,
    uiTag?: IBaseComponentUITag,
    server?: IServer,
    form?: Vue,
    element?: Vue,
): IUITagResult {
    do {
        if (metas == null || metaIndex == null) break;
        if (meta == null) break;
        const name = meta.attr.name;
        const row = data;
        const value = row?.[name];

        /// initial variables
        const variables: IUITagVariables = {
            server,
            hostElement: form,
            element,
        };
        /// initial props
        /// form, provide IFormComponentAttr, which includes row / name / value.
        const baseProps: IUITagResult = {
            props: {
                server,
                row,
                name,
                value,
                meta,
            },
            styles: {},
            specials: {},
        };

        /// initial input
        const baseInput: Omit<IUITagInput, "rule"> = {
            /// rule
            target: EUITagTarget[target],
            metas,
            metaIndex,
            meta,
            inputs: baseProps,
            isDefault: false,
            variables,
            component,
        };
        let finalProps = applyTags(EUITagTarget[target], baseInput, uiTag);

        /// eventually, merge the styles
        return {
            ...finalProps.inputs,
            props: ObjectHelper.deepMerge(finalProps.inputs.props, {
                style: finalProps.inputs.styles,
                /// also provide specials
                specials: finalProps.inputs.specials,
            }),
        };
    } while (0);

    return {};
}

/// on submit, do:
/// 1) @uiSubmitWrap the innate value as result
/// 2) if server configured with C / U / D, perform
/// 3) $emit value
async function funcDoSubmit(this: Form) {
    const metas = this.metas;
    let result = {};

    /// loop through all attributes
    for (let idxMeta = 0; idxMeta < metas.attributes.length; idxMeta++) {
        const inf = metas.attributes[idxMeta];
        const name = inf.attr.name;
        const binding = this.bindFormProperties("FormSubmit", idxMeta);
        let value = ObjectHelper.deepClone(this.innateValue[name]);
        const specials = binding.specials;
        const previousData = this.query.data?.results?.[0]?.[inf.attr.name];

        /// if specials.submitConverter, do convert.
        if (specials?.submitConverter) {
            value = specials?.submitConverter;
        }

        /// on submit, remove specific key
        // remove table PRIVATE_ROW_ID
        ObjectHelper.removeObjectKeys(value, PRIVATE_ROW_ID);

        /// generate target
        let target = {
            [inf.attr.name]: value,
        };

        /// if specials.relationConnect, disconnect & connect.
        if (specials?.relationConnect) {
            const multiple = binding.props?.multiple;
            /// handle specials.relationPair
            const removal =
                specials.relationPair && specials.relationPair?.state !== true;
            funcRelationConnect(previousData, multiple, target, inf.attr.name, removal);
        }

        /// if server method U
        if (
            funcHandleDiffU.call(this, previousData, target[inf.attr.name], specials) ===
            false
        )
            continue;

        /// @uiSubmitWrap the innate value as result
        /// if specials.submitIgnore, remove the value.
        /// if specials.submitWrap, wrap the value.
        target = specials?.submitIgnore
            ? undefined
            : value !== undefined
              ? ObjectHelper.wrap(binding.specials?.submitWrap, target)
              : undefined;

        /// do merge
        if (target != undefined) {
            result = ObjectHelper.deepMerge(result, target);
        }
    }

    /// 2) if server configured with C / U / D, perform
    if (this.server && this.server.path) {
        const server = this.server?.server ?? this.$server;
        const params = this.server.params;
        const path = this.server?.path;
        const execQuery = config.mocking ? "logQuery" : "useQuery";

        /// data cannot be empty on C and U
        if (
            (this.server.method === "C" || this.server.method === "U") &&
            (result as any)?.data == null
        ) {
            (result as any).data = {};
        }

        if (this.server.method === "C") {
            /// if C
            await server[execQuery](
                this.query,
                server.C,
                path as any,
                ObjectHelper.deepMerge(result, params),
            ).then((value) => {
                this.doUpdated(value);
            });
        } else if (this.server.method === "U" && params?.where) {
            /// if U and where criteria exists
            await server[execQuery](
                this.query,
                server.U,
                path as any,
                ObjectHelper.deepMerge(result, params),
            ).then((value) => {
                this.doUpdated(value);
            });
        } else if (this.server.method === "D" && params?.where) {
            /// if D and where criteria exists
            await server[execQuery](
                this.query,
                server.D,
                path as any,
                ObjectHelper.deepMerge(result, params),
            ).then((value) => {
                this.doUpdated(value);
            });
        }
    }

    /// 3) $emit value
    return result;
}

/// on update, only keep the changes value.
/// return true, submit the value.
/// return false, ignore the value.
function funcHandleDiffU(
    this: Form,
    previous: any,
    current: any,
    specials: IUITagResult["specials"],
): boolean {
    do {
        /// if not updating, pass.
        if (!this.server?.path || this.server?.method !== "U") break;

        /// if @uiRelationConnect, pass.
        if (specials?.relationConnect) {
            /// if empty array, ignore.
            if (Array.isArray(current) && current.length === 0) return false;
            break;
        }

        if (ObjectHelper.equals(previous, current)) return false;
    } while (0);
    return true;
}

function funcDoReload(this: Form, value: IServer | undefined) {
    /// if no value, skip.
    if (value == undefined) return;
    /// don't do query on "C"
    if (value.method !== "U" && value.method !== "D") return;
    const params = value.params;
    /// if no where criteria, skip fetching.
    if (params?.where == undefined) return;

    this.$nextTick(() => {
        const server = this.server?.server ?? this.$server;
        const path = this.server?.path;
        server
            .useQuery(this.query, (value.server ?? this.$server).R, path as any, params)
            .then((data: any) => {
                this.updateIndex = this.query.index;
                /// update value
                this.innateValue = data.results[0];
                /// emit reloaded
                this.doReloaded(this.innateValue);
            });
    });
}

/// when server changed with param id, do reload.
function funcOnServerUpdated(
    this: Form,
    newValue: IServer | undefined,
    oldValue: IServer | undefined,
) {
    /// if not changing, skip update.
    if (ObjectHelper.equals(newValue, oldValue)) return;

    /// execute update.
    return funcDoReload.call(this, newValue);
}

/// removal: disconnect only
function funcRelationConnect(previous, multiple, target, name, removal: boolean = false) {
    do {
        let current = removal ? undefined : target[name];
        /// 1) singular case
        if (!multiple) {
            previous = previous ? [previous] : undefined;
            current = current ? [current] : undefined;
            const { removed, inserted } = ArrayHelper.findDiff(
                previous as any[],
                current as any[],
                (a, b) => a.id === b.id,
            );
            if (inserted) {
                target[name] = {
                    connect: ObjectHelper.Pick(inserted[0], "id"),
                };
            } else if (removed) {
                target[name] = {
                    disconnect: ObjectHelper.Pick(removed[0], "id"),
                };
            } else {
                /// don't do update
                target[name] = undefined;
            }

            /// 2) multiple case
        } else {
            const { removed, inserted } = ArrayHelper.findDiff(
                previous as any[],
                current as any[],
                (a, b) => a.id === b.id,
            );
            target[name] = {
                disconnect:
                    removed == null
                        ? undefined
                        : removed.map((o) => ObjectHelper.Pick(o, "id")),
                connect:
                    inserted == null
                        ? undefined
                        : inserted.map((o) => ObjectHelper.Pick(o, "id")),
            };
        }
    } while (0);

    /// finally, remove the undefined value.
    const ins = target[name];
    if (ins != null) {
        if (ins.connect == null) delete ins.connect;
        if (ins.disconnect == null) delete ins.disconnect;
        if (ObjectHelper.isEmpty(ins)) target[name] = undefined;
    }
}

/// handle submit button popovers ///////////////////
// button popover message
function invalidMessages(
    this: Form,
): Array<{ key: string; label: string; message: string }> {
    const states = this.states ?? {};
    const messages = Object.keys(states).reduce((final, key) => {
        const { state, message } = states[key];
        if (state !== false) return final;

        const properties = this.bindFormProperties(
            "FormElement",
            this.metas.getIndexByName(key),
        );
        const label = properties.props?.label;
        final.push({ key, label, message: this.$_(message) });
        return final;
    }, [] as any);
    return messages;
}

function doFocusInvalidField(this: Vue, key: string) {
    const parent: any = this.$refs["form-container"];
    if (parent == null) return;
    const element = parent.querySelector(
        `input[name="${key}"], textarea[name="${key}"], [name="${key}"] input, [name="${key}"] textarea`,
    );
    if (element == null) return;
    element.focus();
}
/////////////////////////////////////////////////////
